<div class="display-mode-switcher" title="Display mode">
  <Segmented raised tag="div">
    {#each MODES as mode (mode.id)}
      <Button on:click={() => setMode(mode.id)}>
        <div class="segment-contents mode-segment" class:selected={currentMode === mode.id}>
          {mode.label}
        </div>
      </Button>
    {/each}
  </Segmented>
</div>
{#if currentMode === 'live-preview'}
<div class="env-control" title="Live preview environment">
  <span class="env-control-label">Live Preview Environment</span>
  <select class="env-control-select" bind:value={$janus4PreviewEnvironmentStore}>
    {#each ENV_OPTIONS as opt (opt.value)}
      <option value={opt.value}>{opt.label}</option>
    {/each}
  </select>
</div>
{#if showSectionOverride}
<div class="env-control" title="Preview this page as it renders under a site section">
  <span class="env-control-label">Preview Section</span>
  <select class="env-control-select" value={sectionSelectValue} on:change={handleSectionChange}>
    <option value="">Default (no section)</option>
    {#each $janus4AvailableSectionsStore as section (section)}
      <option value={section}>{$janus4SectionNamesStore[section] || section}</option>
    {/each}
  </select>
</div>
{/if}
{/if}

<style>
  *, *::before, *::after {
    box-sizing: border-box;
  }

  .display-mode-switcher {
    display: flex;
    align-items: center;
    height: 36px;
  }

  .display-mode-switcher :global(.segmented) {
    height: 100%;
    border-radius: 4px;
  }

  .display-mode-switcher :global(.button) {
    height: 100%;
    padding: 0;
    text-transform: none;
  }

  .segment-contents {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0px 10px;
    font-weight: bold;
    font-size: 13px;
    text-transform: uppercase;
    white-space: nowrap;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  /* The selected display mode uses the bar's neutral selection fill, matching
     the viewport width switcher. */
  .mode-segment.selected {
    background-color: #d0d0d0;
  }

  /* Two-line labeled dropdown that stays within the row height and reads as
     part of the Live Preview button it sits next to. */
  .env-control {
    display: flex;
    flex-direction: column;
    /* Pin the label to the top and the dropdown to the bottom so the control
       spans the full row height, aligning with the buttons beside it. */
    justify-content: space-between;
    align-items: stretch;
    height: 36px;
    margin-left: 8px;
  }

  .env-control-label {
    font-size: 9px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #666;
    white-space: nowrap;
    padding-left: 1px;
  }

  .env-control-select {
    -webkit-appearance: none;
    -moz-appearance: none;
         appearance: none;
    height: 22px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    color: black;
    padding: 0 22px 0 8px;
    border: none;
    border-radius: 4px;
    background-color: #f1f1f1;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23333' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 7px center;
    background-size: 9px 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
    cursor: pointer;
  }</style>

<script lang="ts">// Single switcher for the three editor display modes:
//   - Schematic Mode:    outline/placeholder view of the page structure
//   - Visual Edit Mode:  real in-app render with hover editing
//   - Live Preview Mode: read-only render on the janus4 engine (iframe)
import { Segmented, Button } from 'framework7-svelte';
import { CardsPageContextKey } from './context';
import { getContext } from 'svelte';
import { janus4PreviewEnvironmentStore } from './Janus4PreviewEnvironment';
import { janus4AvailableSectionsStore, janus4CmsSectionStore, janus4SectionOverrideStore, janus4SectionNamesStore } from './Janus4PreviewSection';
const MODES = [
    { id: 'schematic', label: 'Schematic' },
    { id: 'visual-edit', label: 'Visual Edit' },
    { id: 'live-preview', label: 'Live Preview' },
];
const ENV_OPTIONS = [
    { value: 'prod', label: 'Production' },
    { value: 'staging', label: 'Staging' },
    { value: 'test', label: 'Test' },
];
const pageContext = getContext(CardsPageContextKey);
const editorModeManager = pageContext.editorModeManager;
const previewStore = editorModeManager.getPreviewStore();
const janus4PreviewStore = editorModeManager.getJanus4PreviewStore();
let currentMode;
$: currentMode = $janus4PreviewStore ? 'live-preview' : ($previewStore ? 'visual-edit' : 'schematic');
function setMode(mode) {
    switch (mode) {
        case 'schematic':
            editorModeManager.setJanus4Preview(false);
            editorModeManager.setPreview(false);
            break;
        case 'visual-edit':
            editorModeManager.setJanus4Preview(false);
            editorModeManager.setPreview(true);
            break;
        case 'live-preview':
            editorModeManager.setJanus4Preview(true);
            break;
    }
}
// The section-override dropdown is offered only when the deployment reports
// site sections AND the page isn't already authored under one of them (i.e.
// it's a shared/default-env page that sections borrow via fallback).
$: isSectionPage = !!$janus4CmsSectionStore && $janus4AvailableSectionsStore.indexOf($janus4CmsSectionStore) !== -1;
$: showSectionOverride = $janus4AvailableSectionsStore.length > 0 && !isSectionPage;
// Clamp the displayed value to a section this deployment actually reports so
// a stale persisted override shows as Default rather than a blank option.
$: sectionSelectValue = ($janus4AvailableSectionsStore.indexOf($janus4SectionOverrideStore) !== -1)
    ? $janus4SectionOverrideStore
    : '';
function handleSectionChange(e) {
    janus4SectionOverrideStore.set(e.target.value || '');
}
</script>
